import { pulseTooltipRules, dimLabel, isPlaceholderDim, cucumberSizeTokens } from './pulse/index.js';

export { dimLabel, cucumberSizeTokens };

/** Cap unique SKUs stored per lineKey per day so tomatoes ~1022 cannot explode the tip. */
export const PULSE_SKU_CAP = 24;

export function pulseSkuFromItem(item, priceTypeValue, priceValue) {
  return {
    variety: item?.variety || '',
    sizeGroup: item?.size_group || '',
    sizeGranularity: item?.size_granularity || '',
    sizeExtraInfo: item?.size_extra_info || '',
    grade: item?.grade || '',
    size: item?.item_size || item?.size || '',
    storage: item?.storage || '',
    condition: item?.condition || '',
    environment: item?.environment || '',
    quality: item?.quality || '',
    appearance: item?.appearance || '',
    properties: item?.properties || '',
    package: item?.package || item?.sku || '',
    kind: priceTypeValue === 'low_price' ? 'low' : priceTypeValue === 'high_price' ? 'high' : 'price',
    value: priceValue,
  };
}

function skuId(lineKey, rec) {
  return `${lineKey}\0${rec.variety}\0${rec.grade}\0${rec.size}\0${rec.storage}\0${rec.condition}\0${rec.environment}\0${rec.quality}\0${rec.appearance}\0${rec.properties}\0${rec.package || ''}\0${rec.kind}`;
}

/** seen is a Set local to the chartData memo — never stored on the row. */
export function pulseRowOf(entry) {
  const p = entry?.payload;
  if (p?.pulseSku) return p;
  if (p?.payload?.pulseSku) return p.payload;
  if (p?.__sku) return p;
  if (p?.payload?.__sku) return p.payload;
  return p;
}

export function pushPulseSku(dateEntry, lineKey, rec, seen) {
  if (!dateEntry.pulseSku) dateEntry.pulseSku = {};
  const slot = dateEntry.pulseSku[lineKey] || (dateEntry.pulseSku[lineKey] = { rows: [], more: 0 });
  const id = `${dateEntry.sortKey ?? dateEntry.date}\0${skuId(lineKey, rec)}`;
  if (seen.has(id)) return;
  seen.add(id);
  if (slot.rows.length >= PULSE_SKU_CAP) {
    slot.more += 1;
    return;
  }
  slot.rows.push(rec);
}

export function sizeHeading(size) {
  const label = dimLabel(size);
  if (!label) return '';
  if (/^size\s*:/i.test(label)) return label.replace(/^size\s*:\s*/i, '');
  return label;
}

/** Storage / condition / environment / quality / appearance — properties nest. */
export function extrasLabel(rec) {
  const bits = [];
  if (!isPlaceholderDim(rec?.storage)) bits.push(rec.storage);
  if (!isPlaceholderDim(rec?.condition)) bits.push(rec.condition);
  if (!isPlaceholderDim(rec?.environment)) bits.push(rec.environment);
  if (!isPlaceholderDim(rec?.quality)) bits.push(rec.quality);
  if (!isPlaceholderDim(rec?.appearance)) bits.push(rec.appearance);
  return bits.join(' · ');
}

export function propertiesHeading(properties) {
  if (isPlaceholderDim(properties)) return '';
  const label = dimLabel(properties);
  if (!label) return '';
  if (/^properties\s*:/i.test(label)) return label.replace(/^properties\s*:\s*/i, '');
  return label;
}

export function gradeHeading(grade) {
  const label = dimLabel(grade);
  if (!label) return 'Not Classified';
  return label;
}

function districtFromDataKey(dataKey) {
  const s = String(dataKey || '');
  const extra = s.indexOf('::');
  const head = extra === -1 ? s : s.slice(0, extra);
  const lowLoc = head.lastIndexOf('_low_price_');
  if (lowLoc !== -1) return `${head.slice(0, lowLoc)} · ${head.slice(lowLoc + 11)}`;
  const highLoc = head.lastIndexOf('_high_price_');
  if (highLoc !== -1) return `${head.slice(0, highLoc)} · ${head.slice(highLoc + 12)}`;
  if (head.endsWith('_low_price')) return head.slice(0, -10);
  if (head.endsWith('_high_price')) return head.slice(0, -11);
  return head;
}

export function groupPulseSkuTooltip(visibleEntries) {
  const districts = [];
  const dIndex = new Map();
  for (const entry of visibleEntries || []) {
    const dataKey = String(entry.dataKey || '');
    const row = pulseRowOf(entry);
    const bag = row?.pulseSku || row?.__sku;
    let slot = bag?.[dataKey];
    if (!slot && bag) {
      const kind = dataKey.includes('_high_price') || String(entry.name || '').includes('High') ? '_high_price' : '_low_price';
      const hit = Object.keys(bag).find((k) => k.includes(kind) && (dataKey ? k === dataKey || k.startsWith(dataKey.split('_low')[0]) : false));
      slot = hit ? bag[hit] : null;
    }
    const records = slot?.rows?.length
      ? slot.rows
      : [{
          variety: '',
          grade: '',
          size: '',
          storage: '',
          condition: '',
          environment: '',
          quality: '',
          appearance: '',
          properties: '',
          kind: String(dataKey).includes('_low_price') ? 'low' : String(dataKey).includes('_high_price') ? 'high' : 'price',
          value: entry.value,
        }];
    const more = slot?.more || 0;
    const district = districtFromDataKey(dataKey);
    let d = dIndex.get(district);
    if (!d) {
      d = {
        key: district,
        label: district,
        more: 0,
        varieties: [],
        vIndex: new Map(),
      };
      dIndex.set(district, d);
      districts.push(d);
    }
    d.more += more;
    for (const rec of records) {
      const vKey = rec.variety || '_';
      let v = d.vIndex.get(vKey);
      if (!v) {
        v = { key: vKey, label: dimLabel(rec.variety), grades: [], gIndex: new Map() };
        d.vIndex.set(vKey, v);
        d.varieties.push(v);
      }
      const gKey = rec.grade || '_';
      let g = v.gIndex.get(gKey);
      if (!g) {
        g = { key: gKey, label: gradeHeading(rec.grade), sizes: [], sIndex: new Map() };
        v.gIndex.set(gKey, g);
        v.grades.push(g);
      }
      const extras = extrasLabel(rec);
      const sKey = `${rec.size || '_'}\0${rec.storage || ''}\0${rec.condition || ''}\0${rec.environment || ''}\0${rec.quality || ''}\0${rec.appearance || ''}`;
      let s = g.sIndex.get(sKey);
      if (!s) {
        s = {
          key: sKey,
          label: sizeHeading(rec.size),
          extras,
          low: null,
          high: null,
          plain: null,
          lowColor: null,
          highColor: null,
          props: [],
          pIndex: new Map(),
        };
        g.sIndex.set(sKey, s);
        g.sizes.push(s);
      }
      const propHead = propertiesHeading(rec.properties);
      let leaf = s;
      if (propHead) {
        let p = s.pIndex.get(propHead);
        if (!p) {
          p = { key: propHead, label: propHead, low: null, high: null, plain: null, lowColor: null, highColor: null };
          s.pIndex.set(propHead, p);
          s.props.push(p);
        }
        leaf = p;
      }
      if (rec.kind === 'low') {
        leaf.low = rec.value;
        leaf.lowColor = entry.color;
      } else if (rec.kind === 'high') {
        leaf.high = rec.value;
        leaf.highColor = entry.color;
      } else {
        leaf.plain = rec.value;
      }
    }
  }
  return districts;
}

/** Size group vs size: one label when they duplicate; both when class + count. */
export function sizePathTokens(sizeGroup, size) {
  const g = dimLabel(sizeGroup);
  const s = dimLabel(size);
  if (!g && !s) return [];
  if (!g) return [s];
  if (!s) return [g];
  const gn = g.toLowerCase().replace(/\s+/g, ' ').trim();
  const sn = s.toLowerCase().replace(/\s+/g, ' ').trim();
  if (gn === sn) return [s];
  if (gn.includes(sn) || sn.includes(gn)) return [g.length >= s.length ? g : s];
  const classy = /^(large|small|medium)\b/i.test(g);
  const count = /\d/.test(s);
  if (classy && count) return [g, s];
  return [s];
}

const LINE_DIMS = ['storage', 'condition', 'environment', 'quality', 'appearance', 'properties'];

function extrasBits(rec, varietyLabel, rules) {
  const hide = new Set(rules.hideLineDims || []);
  const bits = [];
  const dims = rules.extrasDimOrder || LINE_DIMS;
  for (const dim of dims) {
    if (hide.has(dim)) continue;
    const label = dimLabel(rec?.[dim]);
    if (isPlaceholderDim(rec?.[dim])) continue;
    if (dim === 'environment' && label === varietyLabel) continue;
    if (dim === 'quality' && rules.qualityLabel) {
      const q = rules.qualityLabel(rec.quality);
      if (q) bits.push(q);
      continue;
    }
    bits.push(label);
  }
  return bits;
}

function maxHigh(block) {
  let m = null;
  for (const r of block.rows || []) {
    if (r.high != null && Number.isFinite(Number(r.high)) && (m == null || r.high > m)) m = r.high;
  }
  for (const b of block.blocks || []) {
    const h = maxHigh(b);
    if (h != null && (m == null || h > m)) m = h;
  }
  return m;
}

function sortBlocksByHigh(blocks) {
  return [...(blocks || [])]
    .sort((a, b) => {
      const ha = maxHigh(a);
      const hb = maxHigh(b);
      if (ha == null && hb == null) return 0;
      if (ha == null) return 1;
      if (hb == null) return -1;
      return hb - ha;
    })
    .map((b) => ({ ...b, blocks: sortBlocksByHigh(b.blocks || []) }));
}

export function rowReportDate(item) {
  const raw = item?.date || item?.date_string || item?.report_date;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : new Date(t);
}

export const VARIETY_SELECT_MAX = 2;

function fieldKey(row, field) {
  if (field === 'package') return String(row?.package || row?.sku || '').trim();
  return String(row?.[field] || '').trim();
}

/** Top `n` values of `field` by row count in the last `days` before the newest date. */
export function pickTopKeysByRecentPoints(rows, field, n = VARIETY_SELECT_MAX, days = 31) {
  const list = Array.isArray(rows) ? rows : [];
  const dated = [];
  let newest = null;
  for (const r of list) {
    const d = rowReportDate(r);
    if (!d) continue;
    dated.push({ r, d });
    if (!newest || d > newest) newest = d;
  }
  const cutoff = newest ? new Date(newest.getTime() - days * 86400000) : null;
  const windowed = cutoff ? dated.filter((x) => x.d >= cutoff).map((x) => x.r) : [];
  const src = windowed.length ? windowed : (dated.length ? dated.map((x) => x.r) : list);
  const counts = new Map();
  for (const r of src) {
    const p = fieldKey(r, field);
    if (!p || isPlaceholderDim(p)) continue;
    counts.set(p, (counts.get(p) || 0) + 1);
  }
  return [...counts.keys()]
    .sort((a, b) => {
      const d = (counts.get(b) || 0) - (counts.get(a) || 0);
      return d !== 0 ? d : a.localeCompare(b);
    })
    .slice(0, Math.max(0, n));
}

/** Package with the most rows in the last `days` before the newest date in scope. */
export function pickPackageWithMostRecentMonthPoints(rows, days = 31) {
  return pickTopKeysByRecentPoints(rows, 'package', 1, days)[0] || '';
}

export function defaultPulseVarieties(rows) {
  return pickTopKeysByRecentPoints(rows, 'variety', VARIETY_SELECT_MAX);
}

function leafKey(rec) {
  return [rec.variety, rec.sizeGroup, rec.sizeGranularity, rec.size, rec.grade, rec.storage, rec.condition, rec.environment, rec.quality, rec.appearance, rec.properties, rec.package || ''].join('\0');
}

function priceRow(leaf, indent) {
  return {
    label: '',
    low: leaf.low,
    high: leaf.high,
    plain: leaf.plain,
    lowColor: leaf.lowColor,
    highColor: leaf.highColor,
    indent,
  };
}

/** Display only: hide placeholder tokens. leaf.path (fold/merge keys) is untouched. */
function showPath(tokens) {
  return tokens.filter((t) => !isPlaceholderDim(t)).join(' · ');
}

function loneBlock(leaf) {
  return {
    heading: showPath(leaf.path),
    rows: [priceRow(leaf, leaf.path.length)],
    blocks: [],
  };
}

function sharedPrefixLen(leaves) {
  if (!leaves.length) return 0;
  const a = leaves[0].path;
  let L = a.length;
  for (let i = 1; i < leaves.length; i++) {
    const b = leaves[i].path;
    let n = 0;
    while (n < L && n < b.length && a[n] === b[n]) n += 1;
    L = n;
  }
  return L;
}

function foldLeaves(leaves) {
  if (!leaves.length) return [];
  if (leaves.length === 1) return [loneBlock(leaves[0])];
  const L = sharedPrefixLen(leaves);
  const ended = leaves.filter((x) => x.path.length === L);
  const rest = leaves.filter((x) => x.path.length > L);
  if (!rest.length) {
    if (ended.length === 1) return [loneBlock(ended[0])];
    return ended.map((leaf) => ({
      heading: showPath(leaf.path),
      rows: [priceRow(leaf, leaf.path.length)],
      blocks: [],
    }));
  }
  const groups = new Map();
  for (const leaf of rest) {
    const tok = leaf.path[L];
    if (!groups.has(tok)) groups.set(tok, []);
    groups.get(tok).push(leaf);
  }
  const buckets = [...groups.values()];
  const allSingles = buckets.length > 0 && buckets.every((b) => b.length === 1);
  if (buckets.length > 1 && allSingles && ended.length === 0 && L >= 1) {
    return [{
      heading: showPath(leaves[0].path.slice(0, L)),
      rows: [],
      blocks: buckets.map((b) => ({
        heading: showPath(b[0].path.slice(L)),
        rows: [priceRow(b[0], b[0].path.length)],
        blocks: [],
      })),
    }];
  }
  if (buckets.length > 1) {
    return [...ended.map(loneBlock), ...buckets.flatMap(foldLeaves)];
  }
  const only = buckets[0];
  if (ended.length) {
    return [...ended.map(loneBlock), ...foldLeaves(only)];
  }
  return foldLeaves(only);
}

function collectLeaves(districtMap, rules, districtLabel) {
  const leaves = [];
  const merge = new Map();
  for (const rec of districtMap.values()) {
    const varietyRaw = dimLabel(rec.variety);
    const variety = rules.varietyLabel ? rules.varietyLabel(rec.variety) : varietyRaw;
    const sizeTok = rules.sizeTokens ? rules.sizeTokens(rec) : sizePathTokens(rec.sizeGroup, rec.size);
    const env = dimLabel(rec.environment);
    let path = [variety, ...sizeTok].filter(Boolean);
    const sameAsEnv = rules.varietySameAsEnv
      ? rules.varietySameAsEnv(varietyRaw, env)
      : Boolean(rules.dropVarietyIfSameAsEnv && env && varietyRaw.toLowerCase() === env.toLowerCase());
    if (sameAsEnv) {
      path = sizeTok.filter(Boolean);
    }
    const extras = extrasBits(rec, variety, rules).filter((t) => t && !path.includes(t));
    const pathFull = [...path, ...extras];
    const k = leafKey(rec);
    let leaf = merge.get(k);
    if (!leaf) {
      leaf = {
        path: pathFull,
        environment: rec.environment,
        district: districtLabel,
        low: null,
        high: null,
        plain: null,
        lowColor: null,
        highColor: null,
      };
      merge.set(k, leaf);
      leaves.push(leaf);
    }
    if (rec.kind === 'low') {
      leaf.low = rec.value;
      leaf.lowColor = rec.color;
    } else if (rec.kind === 'high') {
      leaf.high = rec.value;
      leaf.highColor = rec.color;
    } else {
      leaf.plain = rec.value;
    }
  }
  return leaves;
}

function entriesFromPulseBag(visibleEntries) {
  const list = visibleEntries || [];
  const first = list[0];
  const row = pulseRowOf(first);
  const bag = row?.pulseSku || row?.__sku;
  if (!bag || typeof bag !== 'object') return list;
  const colorByKey = new Map();
  for (const e of list) {
    if (e?.dataKey) colorByKey.set(String(e.dataKey), e.color);
  }
  return Object.keys(bag).map((dataKey) => ({
    dataKey,
    color: colorByKey.get(dataKey),
    value: first?.payload?.[dataKey] ?? row?.[dataKey],
    payload: first?.payload,
  }));
}

export function flattenPulseTooltip(visibleEntries, commodity) {
  const districts = [];
  const dIndex = new Map();
  for (const entry of entriesFromPulseBag(visibleEntries)) {
    const dataKey = String(entry.dataKey || '');
    const row = pulseRowOf(entry);
    const bag = row?.pulseSku || row?.__sku;
    let slot = bag?.[dataKey];
    if (!slot && bag) {
      const kind = dataKey.includes('_high_price') || String(entry.name || '').includes('High') ? '_high_price' : '_low_price';
      const hit = Object.keys(bag).find((k) => k.includes(kind) && (dataKey ? k === dataKey || k.startsWith(dataKey.split('_low')[0]) : false));
      slot = hit ? bag[hit] : null;
    }
    const records = slot?.rows?.length
      ? slot.rows
      : [{
          variety: '',
          sizeGroup: '',
          grade: '',
          size: '',
          storage: '',
          condition: '',
          environment: '',
          quality: '',
          appearance: '',
          properties: '',
          kind: String(dataKey).includes('_low_price') ? 'low' : String(dataKey).includes('_high_price') ? 'high' : 'price',
          value: entry.value,
        }];
    const district = districtFromDataKey(dataKey);
    let d = dIndex.get(district);
    if (!d) {
      d = { key: district, label: district, more: 0, recs: new Map() };
      dIndex.set(district, d);
      districts.push(d);
    }
    d.more += slot?.more || 0;
    for (const rec of records) {
      const id = leafKey(rec);
      const prev = d.recs.get(id) || { ...rec, color: entry.color };
      if (rec.kind === 'low') {
        prev.kind = 'low';
        prev.value = rec.value;
        prev.color = entry.color;
        prev.low = rec.value;
        prev.lowColor = entry.color;
      } else if (rec.kind === 'high') {
        prev.high = rec.value;
        prev.highColor = entry.color;
      }
      d.recs.set(`${id}\0${rec.kind}`, { ...rec, color: entry.color });
    }
  }
  const rules = pulseTooltipRules(commodity);
  return districts.map((d) => {
    const leaves = collectLeaves(d.recs, rules, d.label);
    return {
      key: d.key,
      label: d.label,
      more: d.more,
      blocks: rules.sectionLeaves
        ? rules.sectionLeaves(leaves, foldLeaves, sortBlocksByHigh)
        : foldLeaves(leaves),
    };
  });
}
